import React, { useState, useEffect, useCallback } from 'react';
import CampaignsTable from './CampaignsTable';
import AutomationsTable from './AutomationsTable';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

// Updated interface for campaign data from API - matches actual response
interface CampaignData {
  clickRate: number;
  sentAt: string;
  name: string;
  openRate: number;
  triggerType: string;
  status: string;
  deliveries: number;
}

// Updated interface for automation data from API - matches actual response
interface AutomationData {
  clickRate: number;
  sentAt: string;
  name: string;
  openRate: number;
  triggerType: string;
  status: string;
  deliveries: number;
}

// Updated interface for campaign API response - it's an array directly
type CampaignsResponse = CampaignData[];

// Updated interface for automation API response - wrapped in items object
interface AutomationsResponse {
  items: AutomationData[];
  total: number;
}

// Grain type for API calls - Full range for campaigns
type CampaignGrainType = 'PT1H' | 'PT24H' | 'PT168H' | 'PT720H' | 'PT8760H';

// Grain type for automations - Only HOUR and DAY
type AutomationGrainType = 'HOUR' | 'DAY';

// Time range type for automations
type AutomationTimeRange = 'PT1H' | 'PT24H' | 'P7D' | 'P30D';

// Function to get date range for automations based on time range
const getAutomationDateRangeByTimeRange = (timeRange: AutomationTimeRange) => {
  const now = new Date();
  let from: Date;

  switch (timeRange) {
    case 'PT1H':
      from = new Date(now);
      from.setHours(from.getHours() - 1);
      break;
    case 'PT24H':
      from = new Date(now);
      from.setDate(from.getDate() - 1);
      break;
    case 'P7D':
      from = new Date(now);
      from.setDate(from.getDate() - 7);
      break;
    case 'P30D':
      from = new Date(now);
      from.setDate(from.getDate() - 30);
      break;
    default:
      from = new Date(now);
      from.setDate(from.getDate() - 1);
      break;
  }

  return {
    from: from.toISOString(),
    to: now.toISOString(),
  };
};

// Function to get grain from time range
const getGrainFromTimeRange = (timeRange: AutomationTimeRange): AutomationGrainType => {
  switch (timeRange) {
    case 'PT1H':
    case 'PT24H':
      return 'HOUR';
    case 'P7D':
    case 'P30D':
      return 'DAY';
    default:
      return 'HOUR';
  }
};

const Reports: React.FC = () => {
  // State to manage the currently active tab ('automations' or 'campaigns')
  const [activeTab, setActiveTab] = useState<'automations' | 'campaigns'>(
    'automations'
  );

  // Campaign filter state
  const [selectedCampaignGrain, setSelectedCampaignGrain] = useState<CampaignGrainType>('PT168H');

  // Automation filter state
  const [selectedAutomationTimeRange, setSelectedAutomationTimeRange] = useState<AutomationTimeRange>('P7D');

  // State for campaigns data
  const [campaignsData, setCampaignsData] = useState<CampaignData[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState<boolean>(true);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);

  // State for automations data
  const [automationsData, setAutomationsData] = useState<AutomationData[]>([]);
  const [automationsLoading, setAutomationsLoading] = useState<boolean>(true);
  const [automationsError, setAutomationsError] = useState<string | null>(null);

  // State for total counts
  const [campaignsTotalCount, setCampaignsTotalCount] = useState<number>(0);
  const [automationsTotalCount, setAutomationsTotalCount] = useState<number>(0);

  // Base URL for API calls

  // Function to get date range based on selected grain for campaigns
  const getCampaignDateRange = useCallback((grain: CampaignGrainType) => {
    const now = new Date();
    let from: Date;

    switch (grain) {
      case 'PT1H':
        from = new Date(now);
        from.setHours(from.getHours() - 1);
        break;
      case 'PT24H':
        from = new Date(now);
        from.setDate(from.getDate() - 1);
        break;
      case 'PT168H':
        from = new Date(now);
        from.setDate(from.getDate() - 7);
        break;
      case 'PT720H':
        from = new Date(now);
        from.setDate(from.getDate() - 30);
        break;
      case 'PT8760H':
        from = new Date(now);
        from.setFullYear(from.getFullYear() - 1);
        break;
      default:
        from = new Date(now);
        from.setDate(from.getDate() - 1);
        break;
    }

    return {
      from: from.toISOString(),
      to: now.toISOString(),
    };
  }, []);

  // Function to fetch campaigns data
  const fetchCampaigns = useCallback(async (grain: CampaignGrainType) => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setCampaignsError('Authentication required');
      setCampaignsLoading(false);
      return;
    }

    setCampaignsLoading(true);
    setCampaignsError(null);

    try {
      const { from, to } = getCampaignDateRange(grain);

      // Construct the API URL with grain parameter
      const url = `${BASE_URL}/v1/reporting/campaigns?tenantId=${tenantId}&grain=${grain}&from=${from}&to=${to}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Response is an array directly
      const data: CampaignsResponse = await response.json();

      // Data is already an array
      setCampaignsData(data || []);
      setCampaignsTotalCount(data?.length || 0);
    } catch (err: any) {
      // console.error('❌ Failed to fetch campaigns:', err);
      setCampaignsError(err.message || 'Failed to load campaigns data');
      toast.error('Failed to load campaigns data');
      setCampaignsData([]);
      setCampaignsTotalCount(0);
    } finally {
      setCampaignsLoading(false);
    }
  }, [getCampaignDateRange]);

  // Function to fetch automations data with time range
  const fetchAutomations = useCallback(async (timeRange: AutomationTimeRange) => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setAutomationsError('Authentication required');
      setAutomationsLoading(false);
      return;
    }

    setAutomationsLoading(true);
    setAutomationsError(null);

    try {
      const { from, to } = getAutomationDateRangeByTimeRange(timeRange);
      const grain = getGrainFromTimeRange(timeRange);

      // Construct the API URL with grain parameter
      const url = `${BASE_URL}/v1/reporting/automations?tenantId=${tenantId}&from=${from}&to=${to}&grain=${grain}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Response is wrapped in { items: [], total: 0 }
      const data: AutomationsResponse = await response.json();

      // Extract items from the response
      setAutomationsData(data.items || []);
      setAutomationsTotalCount(data.total || 0);
    } catch (err: any) {
      // console.error('❌ Failed to fetch automations:', err);
      setAutomationsError(err.message || 'Failed to load automations data');
      toast.error('Failed to load automations data');
      setAutomationsData([]);
      setAutomationsTotalCount(0);
    } finally {
      setAutomationsLoading(false);
    }
  }, []);

  // Fetch campaigns when campaign grain changes
  useEffect(() => {
    fetchCampaigns(selectedCampaignGrain);
  }, [selectedCampaignGrain, fetchCampaigns]);

  // Fetch automations when time range changes
  useEffect(() => {
    fetchAutomations(selectedAutomationTimeRange);
  }, [selectedAutomationTimeRange, fetchAutomations]);

  // Transform campaign data for the table - show 0% instead of dash
  const transformCampaignsForTable = (campaigns: CampaignData[]) => {
    return campaigns.map((campaign, index) => ({
      id: `campaign_${index}`,
      name: campaign.name || 'Unnamed Campaign',
      type: 'Email Blast',
      status: campaign.status === 'UNKNOWN' ? 'Draft' : 'Active',
      recipients: campaign.deliveries?.toString() || '0',
      openRate: `${(campaign.openRate * 100).toFixed(1)}%`,
      clickRate: `${(campaign.clickRate * 100).toFixed(1)}%`,
      sentAt: campaign.sentAt || '—',
      triggerType: campaign.triggerType || '—',
      action: 'View',
      rawData: campaign
    }));
  };

  // Transform automation data for the table - show 0% instead of dash
  const transformAutomationsForTable = (automations: AutomationData[]) => {
    return automations.map((automation, index) => ({
      id: `automation_${index}`,
      name: automation.name || 'Unnamed Automation',
      type: 'Trigger',
      status: automation.status === 'UNKNOWN' ? 'Draft' : 'Active',
      recipients: automation.deliveries?.toString() || '0',
      openRate: `${(automation.openRate * 100).toFixed(1)}%`,
      clickRate: `${(automation.clickRate * 100).toFixed(1)}%`,
      sentAt: automation.sentAt || '—',
      triggerType: automation.triggerType || '—',
      action: 'View',
      rawData: automation
    }));
  };

  // Function to handle tab switching
  const handleTabSwitch = (tab: 'automations' | 'campaigns') => {
    setActiveTab(tab);
  };

  // Function to handle campaign grain change
  const handleCampaignGrainChange = (grain: CampaignGrainType) => {
    setSelectedCampaignGrain(grain);
  };

  // Function to handle automation time range change
  const handleAutomationTimeRangeChange = (timeRange: AutomationTimeRange) => {
    setSelectedAutomationTimeRange(timeRange);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchCampaigns(selectedCampaignGrain);
    fetchAutomations(selectedAutomationTimeRange);
  };

  // Get transformed data
  const transformedCampaigns = transformCampaignsForTable(campaignsData);
  const transformedAutomations = transformAutomationsForTable(automationsData);

  // Determine loading and error states for active tab
  const isLoading = activeTab === 'automations' ? automationsLoading : campaignsLoading;
  const hasError = activeTab === 'automations' ? automationsError : campaignsError;
  const data = activeTab === 'automations' ? transformedAutomations : transformedCampaigns;

  return (
    <div className='p-2'>
      {/* Header */}
      <h2 className='text-[24px] font-bold text-[#3F5265]'>Reports</h2>
      <p className='text-gray-500 mb-4'>
        Performance insights for your email campaigns.
      </p>

      {/* Tabs */}
      <div className='flex mb-4'>
        <button
          onClick={() => handleTabSwitch('automations')}
          className={`px-4 py-2 text-sm font-medium ${activeTab === 'automations'
            ? 'border-b-2 border-teal-500 text-teal-600'
            : 'text-gray-500'
            }`}
        >
          Automations
        </button>
        <button
          onClick={() => handleTabSwitch('campaigns')}
          className={`px-4 py-2 text-sm font-medium ${activeTab === 'campaigns'
            ? 'border-b-2 border-teal-500 text-teal-600'
            : 'text-gray-500'
            }`}
        >
          Campaigns
        </button>
      </div>

      {/* Render loading state */}
      {isLoading ? (
        <div className='bg-white rounded-md mt-6 border border-gray-200 shadow-sm p-8'>
          <div className='flex items-center justify-center h-64'>
            <div className='text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto'></div>
              <p className='mt-4 text-gray-500'>Loading {activeTab}...</p>
            </div>
          </div>
        </div>
      ) : hasError ? (
        <div className='bg-white rounded-md mt-6 border border-gray-200 shadow-sm p-8'>
          <div className='bg-red-50 border border-red-200 rounded-md p-4 text-center'>
            <p className='text-red-600'>{hasError}</p>
            <button
              onClick={handleRefresh}
              className='mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'automations' ? (
            <AutomationsTable
              automations={data}
              totalCount={automationsTotalCount}
              isLoading={false}
              onRefresh={handleRefresh}
              selectedTimeRange={selectedAutomationTimeRange}
              onTimeRangeChange={handleAutomationTimeRangeChange}
            />
          ) : (
            <CampaignsTable
              campaigns={data}
              totalCount={campaignsTotalCount}
              isLoading={false}
              onRefresh={handleRefresh}
              selectedGrain={selectedCampaignGrain}
              onGrainChange={handleCampaignGrainChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Reports;